import React, { useState, useRef, useCallback } from 'react';
import { Card, Row, Col, Form, Button } from 'react-bootstrap';
import { FaFileWord, FaTimes, FaCloudUploadAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';

const LABELS_TIPO = {
  fornecedor: 'Carta Fornecedor',
  comercio: 'Carta Comércio',
};

const formatarTamanho = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const UploadTemplateCarta = ({
  tipoCarta,
  codigoGrupoEmpresa,
  filtros,
  ocorrencia,
  emitindo,
  onEmitir,
}) => {
  const [arquivo, setArquivo] = useState(null);
  const [arrastando, setArrastando] = useState(false);
  const inputRef = useRef(null);

  const labelTipo = LABELS_TIPO[tipoCarta] || tipoCarta;

  const validarArquivo = useCallback((file) => {
    if (!file) return false;
    const nome = file.name ? file.name.toLowerCase() : '';
    if (!nome.endsWith('.docx')) {
      toast.error('Apenas documentos Word (.docx) são aceitos.');
      return false;
    }
    return true;
  }, []);

  const processarArquivos = useCallback(
    (files) => {
      if (!files || files.length === 0) return;
      if (files.length > 1) {
        toast.info('Selecione apenas um template. Usando o primeiro arquivo.');
      }
      const file = files[0];
      if (validarArquivo(file)) {
        setArquivo(file);
      }
    },
    [validarArquivo]
  );

  const handleInputChange = useCallback(
    (e) => {
      processarArquivos(Array.from(e.target.files));
      e.target.value = '';
    },
    [processarArquivos]
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setArrastando(false);
      if (emitindo) return;
      processarArquivos(Array.from(e.dataTransfer.files));
    },
    [processarArquivos, emitindo]
  );

  const handleDragOver = useCallback(
    (e) => {
      e.preventDefault();
      if (!emitindo) setArrastando(true);
    },
    [emitindo]
  );

  const handleDragLeave = useCallback(() => setArrastando(false), []);

  const abrirSeletor = useCallback(() => {
    if (!emitindo && inputRef.current) inputRef.current.click();
  }, [emitindo]);

  const removerArquivo = useCallback((e) => {
    e.stopPropagation();
    setArquivo(null);
  }, []);

  const handleGerar = useCallback(() => {
    if (!arquivo) {
      toast.error('Selecione o documento Word (.docx) antes de gerar.');
      return;
    }

    const filtrosPayload = {
      codigo_grupo_empresa: filtros.codigo_grupo_empresa,
      codigo_cidade: filtros.codigo_cidade,
      codigo_ramo: filtros.codigo_ramo,
      codigo_regiao: filtros.codigo_regiao,
      conta_inicial: filtros.conta_inicial,
      conta_final: filtros.conta_final,
      aniver_dia_ini: filtros.aniver_dia_ini,
      aniver_dia_fim: filtros.aniver_dia_fim,
      aniver_mes_ini: filtros.aniver_mes_ini,
      aniver_mes_fim: filtros.aniver_mes_fim,
      imoveis_rescindidos: filtros.imoveis_rescindidos,
      todos_clientes: filtros.todos_clientes,
    };

    // Backend espera a parte 'request' = JSON do CartaEmissaoRequest (tipo_carta + filtros).
    const formData = new FormData();
    formData.append('template', arquivo);
    formData.append('request', JSON.stringify({
      tipo_carta: tipoCarta,
      filtros: filtrosPayload,
    }));

    onEmitir(formData);
  }, [arquivo, filtros, tipoCarta, onEmitir]);

  return (
    <Card className="mt-3">
      <Card.Header>Gerar Cartas — {labelTipo}</Card.Header>
      <Card.Body>
        <p className="text-muted">
          {labelTipo} — selecione o template .docx e clique em Gerar Cartas
        </p>
        <Row>
          <Col xs={12} md={8} lg={6}>
            <Form.Label className="font-weight-bold">Template Word (.docx)</Form.Label>

            <input
              ref={inputRef}
              type="file"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              style={{ display: 'none' }}
              onChange={handleInputChange}
            />

            {!arquivo ? (
              <div
                role="button"
                tabIndex={0}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={abrirSeletor}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') abrirSeletor();
                }}
                style={{
                  border: `2px dashed ${arrastando ? '#007bff' : '#ccc'}`,
                  borderRadius: '6px',
                  padding: '24px',
                  textAlign: 'center',
                  cursor: emitindo ? 'not-allowed' : 'pointer',
                  background: arrastando ? '#f0f8ff' : '#fafafa',
                  transition: 'all 0.2s ease',
                  minHeight: '120px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  opacity: emitindo ? 0.6 : 1,
                }}
              >
                <FaCloudUploadAlt
                  size={36}
                  color={arrastando ? '#007bff' : '#6c757d'}
                />
                <p className="mb-0 mt-2">
                  Arraste o template aqui ou clique para selecionar
                </p>
                <small className="text-muted">Somente arquivos .docx</small>
              </div>
            ) : (
              <div
                className="d-flex align-items-center justify-content-between"
                style={{
                  border: '1px solid #d6e9c6',
                  borderRadius: '6px',
                  padding: '12px 16px',
                  background: '#f3f9ef',
                }}
              >
                <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
                  <FaFileWord size={28} color="#2b579a" className="flex-shrink-0" />
                  <div className="ml-3" style={{ minWidth: 0 }}>
                    <div
                      className="font-weight-bold text-truncate"
                      title={arquivo.name}
                      style={{ maxWidth: '320px' }}
                    >
                      {arquivo.name}
                    </div>
                    <small className="text-muted">
                      {formatarTamanho(arquivo.size)}
                    </small>
                  </div>
                </div>
                <Button
                  variant="link"
                  className="text-danger p-0 ml-3 flex-shrink-0"
                  onClick={removerArquivo}
                  disabled={emitindo}
                  title="Remover arquivo"
                >
                  <FaTimes size={18} />
                </Button>
              </div>
            )}
          </Col>
        </Row>
        <Row className="mt-3">
          <Col xs={12}>
            <Button
              variant="primary"
              onClick={handleGerar}
              disabled={!arquivo || emitindo}
            >
              {emitindo ? 'Gerando...' : 'Gerar Cartas'}
            </Button>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default UploadTemplateCarta;
